import { Component, OnInit } from '@angular/core';
import { CasosService } from './casos.service';
import { Caso, ParticipanteCaso } from './casos.model';
import { ActivatedRoute } from '@angular/router';
import { NotificationsService } from '../core/service/notifications.service';
import { AuthenticationService } from '../core/service/auth.service';
import { User } from '../core/models/auth.models';
import { ParticipantesService } from '../institucion/institucion.service';
import { UsuarioService } from '../usuarios/usuarios.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { getCurrentLanguage } from '../core/helpers/utils';

interface InstitucionConDetalles {
  id_usuario?: string;
  id_parte?: string;
  tipo?: string;
  nombre?: string;
  apellidos?: string;
  email?: string;
  tipo_parte?: string;
  arbitro_rol?: string;
  razon_social?: string
  despacho?: Despacho
  estado?: string;
  nacionalidad?: string;  // Agregar nacionalidad como opcional
  participantes?: ParticipanteDetalles[];  // Agregar participantes como opcional
}

interface ParticipanteDetalles {
  id_usuario: string;
  nombre: string;
  apellidos: string;
  tipo: string;
  despacho?: string;
}

interface Despacho {
  id_usuario: string;
  nombre?: string;
}

@Component({
  selector: 'app-inicio',
  templateUrl: './inicio.component.html',
  styleUrls: ['./inicio.component.scss']
})
export class InicioComponent implements OnInit {
  caso!: Caso;
  loaded: boolean = false;
  error: string = '';

  institucionesList: InstitucionConDetalles[] = [];
  avatarUrls: { [key: string]: SafeUrl } = {}; // Para almacenar las URLs de los avatares
  institucionesLoaded = false; // Controla si las instituciones se cargaron
  isLoadingInstituciones = true; // Controla la visibilidad del spinner

  demandantesList: InstitucionConDetalles[] = [];
  demandantesLoaded = false;
  isLoadingDemandantes = true;

  demandadosList: InstitucionConDetalles[] = [];
  demandadosLoaded = false;
  isLoadingDemandados = true;

  tribunalesList: InstitucionConDetalles[] = [];
  tribunalesLoaded = false;
  isLoadingTribunales = true;

  loggedInUser: User | null = {};

  disableOptions: boolean = true;

  hasReconvencion(): boolean {
    const cuantiaReconvencion = Number(this.caso?.cuantia_reconvencion ?? 0);
    return cuantiaReconvencion > 0;
  }

  constructor(
    private casosService: CasosService,
    private route: ActivatedRoute,
    private notificationsService: NotificationsService,
    private authService: AuthenticationService,
    private participantesService: ParticipantesService, // Servicio para instituciones
    private usuarioService: UsuarioService, // Servicio para obtener avatares de usuarios
    private sanitizer: DomSanitizer // Sanitizar las URLs de los avatares
  ) { }

  ngOnInit(): void {
    const id_caso = this.route.snapshot.paramMap.get('id');
    if (id_caso) {
      this.casosService.getCaso(id_caso).subscribe({
        next: (caso) => {
          this.authService.currentUser().subscribe(user => {
            this.loggedInUser = user;
            if (user?.nivel_acceso) {

              // Si no soy admin desactivo opciones
              if (user.nivel_acceso == "ADMIN") {
                this.disableOptions = false;
              }
            }


          }, error => {
            console.error('Error al obtener el usuario actual:', error);
          });

          this.caso = caso || {};

          if (caso.fecha_solicitud) {
            this.caso.fecha_solicitud = new Date(caso.fecha_solicitud).toISOString().split('T')[0];
          }

          this.loaded = true;

          // Cargar todas las instituciones de una sola vez y luego filtrar los datos
          this.loadInstitucionesYFiltrar();

        },
        error: (error) => {
          console.error('Error al cargar el caso:', error);
          this.error = error;
          this.loaded = false;
        }
      });
    }
  }

  // Método para cargar todas las instituciones y luego filtrar
  loadInstitucionesYFiltrar(): void {
    const id_expediente = this.route.snapshot.paramMap.get('id');
    if (id_expediente) {

      let language = getCurrentLanguage();
      // Filtrar y mapear los datos para cada lista de partes
      this.demandantesList = this.caso.partes
        ?.filter(parte => parte.tipo_parte === 'DEMANDANTE')
        .map(parte => ({
          id_parte: parte.id,
          tipo: 'DEMANDANTE',
          razon_social: parte.razon_social,
          nacionalidad: parte.nacionalidadData?.[language],
          participantes: parte.parteParticipantes?.map(pp => ({
            id_usuario: pp.participante.id_usuario,
            nombre: pp.participante.usuario.nombre,
            apellidos: pp.participante.usuario.apellidos,
            tipo: pp.participante.tipo,
            despacho: pp.participante.despacho?.nombre
          }))
        })) || [];

      this.demandadosList = this.caso.partes
        ?.filter(parte => parte.tipo_parte === 'DEMANDADO')
        .map(parte => ({
          id_parte: parte.id,
          tipo: 'DEMANDADO',
          razon_social: parte.razon_social,
          nacionalidad: parte.nacionalidadData?.[language],
          participantes: parte.parteParticipantes?.map(pp => ({
            id_usuario: pp.participante.id_usuario,
            nombre: pp.participante.usuario.nombre,
            apellidos: pp.participante.usuario.apellidos,
            tipo: pp.participante.tipo,
            despacho: pp.participante.despacho?.nombre
          }))
        })) || [];

      this.tribunalesList = this.filtrarTribunales();
      this.institucionesList = this.filtrarOtrasInstituciones();

      // No espero por la carga de avatares para mostrar la información
      this.demandantesLoaded = true;
      this.isLoadingDemandantes = false;
      this.demandadosLoaded = true;
      this.isLoadingDemandados = false;
      this.tribunalesLoaded = true;
      this.isLoadingTribunales = false;
      this.institucionesLoaded = true;
      this.isLoadingInstituciones = false;

      // Cargar avatares para cada lista
      this.demandantesList.forEach(demandante => {
        this.cargarAvatares(demandante.participantes || [], () => {
          this.demandantesLoaded = true;
          this.isLoadingDemandantes = false;
        });
      });

      this.demandadosList.forEach(demandado => {
        this.cargarAvatares(demandado.participantes || [], () => {
          this.demandadosLoaded = true;
          this.isLoadingDemandados = false;
        });
      });

      this.cargarAvatares(this.tribunalesList, () => {
        this.tribunalesLoaded = true;
        this.isLoadingTribunales = false;
      });

      this.cargarAvatares(this.institucionesList, () => {
        this.institucionesLoaded = true;
        this.isLoadingInstituciones = false;
      });
    }
  }

  // Filtrar demandantes o demandados
  filtrarInstituciones(data: any[], tipoParte: string): InstitucionConDetalles[] {
    return data
      .filter(institucion =>
        institucion.tipo === 'REPRESENTANTE' &&
        Array.isArray(institucion['parteParticipantes']) &&
        institucion['parteParticipantes'].some((pp: any) => pp.parte?.tipo_parte === tipoParte)
      )
      .map(institucion => ({
        id_usuario: institucion.id_usuario,
        tipo: institucion.tipo.replace(/_/g, ' '),
        despacho: institucion.despacho,
        nombre: institucion.usuario.nombre,
        apellidos: institucion.usuario.apellidos,
        email: institucion.usuario.email,
        tipo_parte: tipoParte
      }) as InstitucionConDetalles);
  }

  filtrarTribunales(): InstitucionConDetalles[] {
    return this.caso['participantes']
      ?.filter((participante: ParticipanteCaso) =>
        participante.tipo === 'TRIBUNAL' || participante.tipo === 'SECRETARIO_ADMINISTRATIVO' || participante.tipo === 'EQUIPO_TRIBUNAL'
      )
      .map((participante: ParticipanteCaso) => ({
        id_usuario: participante.id_usuario,
        tipo: participante.tipo.replace(/_/g, ' '), // Formato del tipo
        nombre: participante.usuario?.nombre,
        apellidos: participante.usuario?.apellidos,
        email: '', // No disponible en participantes del JSON
        estado: participante.estado,
        arbitro_rol: participante.arbitro_rol
          ? this.convertirArbitroRol(participante.arbitro_rol, this.caso?.cuantia_reconvencion)
          : ''
      }) as InstitucionConDetalles)
      .sort((a, b) => {
        // Mover los que tienen estado 'SUSTITUIDO' al final
        if (a.estado === 'SUSTITUIDO' && b.estado !== 'SUSTITUIDO') {
          return 1;
        } else if (a.estado !== 'SUSTITUIDO' && b.estado === 'SUSTITUIDO') {
          return -1;
        }
        return 0;
      }) || [];
  }

  filtrarOtrasInstituciones(): InstitucionConDetalles[] {
    return this.caso['participantes']
      ?.filter((participante: ParticipanteCaso) =>
        participante.tipo === 'LETRADO' ||
        participante.tipo === 'ADMINISTRADOR' ||
        participante.tipo === 'SECRETARIA_GENERAL'
      )
      .map((participante: ParticipanteCaso) => ({
        id_usuario: participante.id_usuario,
        tipo: participante.tipo.replace(/_/g, ' '), // Formato del tipo
        nombre: participante.usuario?.nombre,
        apellidos: participante.usuario?.apellidos,
        email: '', // No disponible en participantes del JSON
      }) as InstitucionConDetalles) || [];
  }


  // Cargar avatares para una lista de instituciones
  cargarAvatares(lista: (InstitucionConDetalles | ParticipanteDetalles)[], callback: () => void): void {
    let avatarLoadCount = 0;
    lista.forEach(institucion => {
      if (institucion.id_usuario) {
        //institucion.nombre = usuario.nombre;
        //institucion.apellidos = usuario.apellidos;
        //institucion.email = usuario.email;
        this.usuarioService.getAvatar(institucion.id_usuario).subscribe(avatarBlob => {
          if (avatarBlob) {
            const blobUrl = URL.createObjectURL(avatarBlob);
            if (institucion.id_usuario)
              this.avatarUrls[institucion.id_usuario] = this.sanitizer.bypassSecurityTrustUrl(blobUrl);
          }
          avatarLoadCount++;
          if (avatarLoadCount === lista.length) {
            callback();
          }
        });

      } else {
        avatarLoadCount++;
        if (avatarLoadCount === lista.length) {
          callback();
        }
      }
    });
  }
  
  convertirArbitroRol(arbitroRol: string | undefined, cuantiaReconvencion?: string | number | null): string {
    const hasReconvencion = Number(cuantiaReconvencion ?? 0) > 0;
    const language = getCurrentLanguage();
    const rolesMap: { [key: string]: string } = language === 'en'
      ? {
        'PRESIDENTE': 'PRESIDENT',
        'COARBITRO_1': hasReconvencion ? 'CO-ARBITRATOR CLAIMANT COUNTER-RESPONDENT' : 'CO-ARBITRATOR CLAIMANT',
        'COARBITRO_2': hasReconvencion ? 'CO-ARBITRATOR RESPONDENT COUNTER-CLAIMANT' : 'CO-ARBITRATOR RESPONDENT',
        'ARBITRO_UNICO': 'SOLE ARBITRATOR',
        'ARBITRO_EMERGENCIA': 'EMERGENCY ARBITRATOR'
      }
      : {
        'PRESIDENTE': 'PRESIDENTE',
        'COARBITRO_1': hasReconvencion ? 'CO-ÁRBITRO DEMANDANTE RECONVENIDO' : 'CO-ÁRBITRO DEMANDANTE',
        'COARBITRO_2': hasReconvencion ? 'CO-ÁRBITRO DEMANDADO RECONVINIENTE' : 'CO-ÁRBITRO DEMANDADO',
        'ARBITRO_UNICO': 'ÁRBITRO ÚNICO',
        'ARBITRO_EMERGENCIA': 'ÁRBITRO DE EMERGENCIA'
      };
    return rolesMap[arbitroRol || ''] || '';
  }

  traducirTipos(tipo?: string | null): string {
    if (!tipo) {
      return '';
    }

    let language = getCurrentLanguage();
    const normalizado = tipo.replace(/_/g, ' ');
    if (language !== 'en') {
      return normalizado;
    }

    const traducciones: { [key: string]: string } = {
      'TRIBUNAL': 'TRIBUNAL',
      'SECRETARIO ADMINISTRATIVO': 'ADMINISTRATIVE SECRETARY',
      'EQUIPO TRIBUNAL': 'TRIBUNAL TEAM',
      'LETRADO': 'COUNSEL',
      'ADMINISTRADOR': 'ADMINISTRATOR',
      'SECRETARIA GENERAL': 'GENERAL SECRETARY',
      'REPRESENTANTE': 'REPRESENTATIVE'
    };

    return traducciones[normalizado.toUpperCase()] || normalizado;
  }
}
